import { Box, Button, Tooltip } from '@mui/material'
import { useState, useEffect, useRef, useCallback } from 'react'
import useNavigate from '../hooks/useNavigate'
import Tab from '@mui/material/Tab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { FieldWrapper } from '../fields/FieldWrapper'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

function Form({
  options,
  checkForm,
  setFormError = () => {},
  setCheckForm,
  modeProps = 'normal',
  mode = 'edit',
  setDataParent,
  setIsSubmitParent,
  isSaved,
  onValidStep
}) {
  const navigate = useNavigate()
  const [data, setData] = useState(options.data || {})
  const visibleTabs = options.tabs?.filter((t) => t.isDisplay?.(data) !== false) ?? []

  const initialTabErrors = visibleTabs.reduce((acc, tab) => {
    acc[String(tab.tabIndex)] =
      modeProps === 'wizard' && (!options.data || Object.keys(options.data).length === 0) ? ['Required'] : [] // only add error if data is empty
    return acc
  }, {})

  const [tabErrors, setTabErrors] = useState(initialTabErrors)
  const childRef = useRef({})
  const getFirstVisibleTab = () => visibleTabs[0]?.tabIndex ?? '1'
  const [activeTab, setActiveTab] = useState(String(options.tabDefault ?? getFirstVisibleTab()))

  useEffect(() => setData(options.data || {}), [options.data])
  const onValueChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setData((prev) => ({ ...prev, [e.target.name]: value }))
    setDataParent((prev) => ({ ...prev, [e.target.name]: value }))
  }

  const onDateChange =
    (name, type = 'date') =>
    (value) => {
      let normalized = null

      if (value) {
        if (type === 'date') {
          normalized = dayjs(value).format('YYYY-MM-DD 00:00:00')
        } else {
          normalized = dayjs(value).format('YYYY-MM-DD HH:mm:ss')
        }
      }

      setData((prev) => ({ ...prev, [name]: normalized }))
      setDataParent((prev) => ({ ...prev, [name]: normalized }))
    }

  const handleChangeFile = (field) => async (event) => {
    const file = event.target.files[0]
    if (!file) return
    setData((prev) => ({ ...prev, [field.name]: file }))
    setDataParent((prev) => ({ ...prev, [field.name]: file }))
  }

  const validateFields = async (fields) => {
    if (!fields || fields.length === 0) return { isValid: true, errors: [] }
    const results = await Promise.all(fields.filter((ref) => ref?.validateField).map((ref) => ref.validateField()))
    const errors = results.filter((r) => r !== null)
    return { isValid: errors.length === 0, errors }
  }

  const validateTab = useCallback(async (tab) => {
    const key = tab ?? 'null'
    const fields = childRef.current[key] || []
    const { isValid, errors } = await validateFields(fields)
    setTabErrors((prev) => ({ ...prev, [key]: isValid ? [] : errors }))
    if (modeProps === 'wizard') setFormError(!isValid)
    return isValid
  }, [])

  const submit = useCallback(async () => {
    let allValid = false
    if (options.tabs?.length) {
      const activeValid = await validateTab(activeTab)
      if (modeProps === 'wizard') setFormError(true)
      if (!activeValid) return

      const otherErrorTabs = Object.keys(tabErrors).filter((t) => t !== activeTab && tabErrors[t]?.length)
      if (otherErrorTabs.length) {
        setActiveTab(otherErrorTabs[0])
        setTimeout(() => validateTab(otherErrorTabs[0]), 0)
        if (modeProps === 'wizard') setFormError(true)
        return
      }
      allValid = true
    } else {
      const { isValid } = await validateFields(childRef.current['null'] || [])
      if (!isValid) return
      allValid = true
    }

    setDataParent(data)
    setIsSubmitParent(true)
    if (modeProps === 'wizard') {
      setFormError(false)
      if (allValid && typeof onValidStep === 'function') onValidStep()
    }
  }, [activeTab, data, tabErrors, modeProps, setDataParent, setIsSubmitParent, setFormError, validateTab])

  useEffect(() => {
    if (checkForm && (modeProps === 'wizard' || modeProps === 'dialog')) {
      setCheckForm(false)
      setTimeout(() => {
        submit()
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkForm, modeProps])

  const renderFields = (fields, tabIndex) => {
    const key = tabIndex != null ? String(tabIndex) : 'null'
    childRef.current[key] = []
    return (
      <Box sx={{ mt: 1, mb: 1, ml: 1, display: 'flex', flexDirection: 'column' }}>
        {fields.map((line, i) => (
          <Box key={i} sx={{ display: 'flex', flexDirection: 'row' }}>
            {line.map((field, j) => {
              if (options.tabs && tabIndex != null && field.tab !== tabIndex) return null
              if (field.isDisplay && !field.isDisplay(data)) return null
              return (
                <FieldWrapper
                  key={`${i}-${j}`}
                  mode={mode}
                  data={data}
                  setData={setDataParent}
                  field={field}
                  onDateChange={onDateChange}
                  onValueChange={onValueChange}
                  handleChangeFile={handleChangeFile}
                  ref={(ref) => {
                    if (!childRef.current[key]) childRef.current[key] = []
                    if (ref && !childRef.current[key].includes(ref)) childRef.current[key].push(ref)
                  }}
                  isSaved={isSaved}
                  tabIndex={key}
                />
              )
            })}
          </Box>
        ))}
      </Box>
    )
  }

  const buildFormButtons = () => {
    if (mode === 'view' || ['dialog', 'noButton', 'wizard'].includes(modeProps)) return null
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row-reverse', marginBottom: '10px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Button onClick={() => navigate(-1)} variant='contained' color='secondary'>
            Annuler
          </Button>
          <Button variant='contained' sx={{ ml: 2 }} color='success' onClick={submit}>
            Enregistrer
          </Button>
        </Box>
      </Box>
    )
  }

  const handleTabChange = (event, newTab) => {
    const prevTab = activeTab
    setActiveTab(newTab.toString())
    validateTab(prevTab)
  }

  const buildTabs = () => {
    if (!visibleTabs.length) return null
    return (
      <Box component='form' noValidate sx={{ display: 'flex', flexDirection: 'column', width: '90%', m: '0 auto' }}>
        <TabContext value={activeTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleTabChange}>
              {visibleTabs.map((tab) => {
                if (!tab?.tabIndex) return null
                const key = String(tab.tabIndex)
                const errorsForTab = tabErrors[key] || []
                const hasError = errorsForTab.length > 0
                return (
                  <Tab
                    key={tab.tabName}
                    value={key}
                    label={
                      <Tooltip
                        title={
                          hasError ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {errorsForTab.map((err, idx) => (
                                <div key={idx}>{err}</div>
                              ))}
                            </Box>
                          ) : (
                            ''
                          )
                        }
                        arrow
                        disableHoverListener={!hasError}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: hasError ? 'error.main' : 'inherit'
                          }}
                        >
                          {tab.tabName} {hasError && <ErrorOutlineIcon color='error' fontSize='small' />}
                        </Box>
                      </Tooltip>
                    }
                  />
                )
              })}
            </TabList>
          </Box>
          {visibleTabs.map(
            (tab) =>
              tab?.tabIndex && (
                <TabPanel key={tab.tabName} value={String(tab.tabIndex)}>
                  {renderFields(options.fields, tab.tabIndex)}
                </TabPanel>
              )
          )}
        </TabContext>
        {buildFormButtons()}
      </Box>
    )
  }

  const buildForm = () => (
    <Box component='form' noValidate sx={{ display: 'flex', flexDirection: 'column', width: '90%', m: '0 auto' }}>
      {renderFields(options.fields, null)}
      {buildFormButtons()}
    </Box>
  )

  return options.tabs ? buildTabs() : buildForm()
}

export default Form
